import { useCallback, useEffect, useState } from "react";
import { Table } from "antd";
import { App, Button, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";

import { PageHeader } from "../../../components/PageHeader";
import { ActionColumn, ActionFunction } from "../../../components/ActionColumn";
import { Content } from "../../../components/Content";
import { useModal } from "../../../hooks";
import { ICostCenter, IMeta, IPagination } from "../../../types"
import { api } from "../../../services/api";

import { CostCenterModal } from "../components/CostCenterModal";

export async function getCostCenter(page = 1, perPage = 10) {
  return await api.get<IPagination<ICostCenter>>(
    `/financials/cost-centers/all/`,
    {
      params: {
        page,
        perPage,
      },
    }
  );
}

const CostCenter = () => {
  const { modal, message } = App.useApp();

  const [costCenter, setCostCenter] = useState<ICostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [load, setLoad] = useState(false);
  const [meta, setMeta] = useState<IMeta>({
    current_page: 1,
    per_page: 10,
  });

  const handleChangePage = useCallback(
    async (page: number, pageSize: number) => {
      setLoading(true);
      await getCostCenter(page, pageSize)
        .then(({ data }) => {
          setCostCenter(data?.data);
          setMeta(data?.meta);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    handleChangePage(1, meta.per_page);
  }, [load, meta.per_page, handleChangePage]);

  const handleRefreshData = () => {
    setLoad(!load);
  };

  const { data, isOpen, handleCloseModal, handleOpenModal } =
    useModal<ICostCenter>(handleRefreshData);

  const actions: ActionFunction<ICostCenter> = (record) => {
    return [
      {
        key: "2",
        icon: <EditOutlined />,
        label: "Editar",
        onClick: () => handleOpenModal(record),
      },
      {
        key: "3",
        icon: <DeleteOutlined />,
        label: "Deletar",
        danger: true,
        onClick: () => onDeleteItem(record),
      },
    ];
  };

  const columns: ColumnsType<ICostCenter> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (value) => {
        return (
          <div style={{ color: "#3030DA" }}>
            {value.match(/\d/g).slice(0, 4).join("")}
          </div>
        );
      },
      width: 80,
    },
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Ativo",
      dataIndex: "is_active",
      key: "is_active",
      render: (text) =>
        text === true ? (
          <Tag color={"green"}>SIM</Tag>
        ) : (
          <Tag color={"red"}>NÃO</Tag>
        ),
    },
    ActionColumn(actions),
  ];

  const onDeleteItem = async (record: ICostCenter) => {
    return modal.confirm({
      title: "Atenção!",
      content: (
        <>
          Deseja deletar o centro de custo?
          <br />
          <i>'{record.name}'</i>
        </>
      ),
      onOk: () =>
        api
          .delete(`/financial/cost-center/${record.id}`)
          .catch(() => message.error(`Algo inesperado ocorreu!`))
          .then(handleRefreshData),
    });
  };

  return (
    <>
      <PageHeader
        title="Centro de custo"
        backButton={false}
        actions={[
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => handleOpenModal()}
          >
            Cadastrar centro de custo
          </Button>,
        ]}
        crumb
      />

      <CostCenterModal
        isModalOpen={isOpen}
        onClose={handleCloseModal}
        data={data}
        onRefreshData={handleRefreshData}
      />

      <Content width="calc(100% - 48px)" margin>
        <Table
          pagination={{
            position: ["bottomCenter"],
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 30, 50],
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            onChange: handleChangePage,
          }}
          size={"small"}
          loading={loading}
          rowKey={(record: ICostCenter) => record.id}
          columns={columns}
          dataSource={costCenter}
        />
      </Content>
    </>
  );
};

export { CostCenter };
